import React, { useState, useEffect } from 'react'
import { Box, Button, Group, TextInput, Select, LoadingOverlay, Text, Avatar, Loader } from '@mantine/core'
import { getAllCountryList, createProvince, updateProvince } from '../../../services/location'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX } from '@tabler/icons-react'



const FormProvince = ({ dataDetail, onCloseModal, reloadList }) => {
    const [loading, setLoading] = useState(false)
    const [loadingCountries, setLoadingCountries] = useState(false)
    const [countryList, setCountryList] = useState([])

    // Form state
    const [form, setForm] = useState({
        name: '',
        country_id: null,
    })

    // Validation state
    const [errors, setErrors] = useState({
        name: '',
        country_id: '',
    })

    useEffect(() => {
        fetchCountries()
    }, [])

    useEffect(() => {
        if (dataDetail) {
            setForm({
                name: dataDetail.name || '',
                country_id: dataDetail.country_id ? String(dataDetail.country_id) : null,
            })
        }
    }, [dataDetail])

    const fetchCountries = async () => {
        setLoadingCountries(true)
        try {
            // Fetch a large number of countries to ensure we get them all for the dropdown
            const response = await getAllCountryList({ 'page[limit]': 300 })
            if (response && response.data) {
                const mappedCountries = response.data.map(c => ({
                    value: String(c.attributes.id),
                    label: c.attributes.name,
                    iso_code_2: c.attributes.iso_code_2
                }))
                setCountryList(mappedCountries)
            }
        } catch (error) {
            console.error("Failed to fetch countries", error)
            notifications.show({
                title: 'Error',
                message: 'Failed to load country list',
                color: 'red',
            })
        } finally {
            setLoadingCountries(false)
        }
    }

    const handleChange = (name, val) => {
        setForm(prev => ({ ...prev, [name]: val }))
        // Clear error when user fills
        if (val) {
            setErrors(prev => ({ ...prev, [name]: '' }))
        }
    }

    const validate = () => {
        let isValid = true
        const newErrors = { name: '', country_id: '' }

        if (!form.name || form.name.trim() === '') {
            newErrors.name = 'Province Name is required'
            isValid = false
        }

        if (!form.country_id) {
            newErrors.country_id = 'Country is required'
            isValid = false
        }

        setErrors(newErrors)
        return isValid
    }

    const handleSubmit = async () => {
        if (!validate()) return

        setLoading(true)
        try {
            const payload = {
                country_id: parseInt(form.country_id),
                name: form.name,
                kiriminaja_id: 0 // As requested
            }

            let response
            if (dataDetail) {
                // Update
                response = await updateProvince(dataDetail.id, payload)
            } else {
                // Create
                response = await createProvince(payload)
            }

            if (response.success) {
                notifications.show({
                    title: 'Success',
                    message: dataDetail ? 'Province updated successfully' : 'Province created successfully',
                    color: 'teal',
                    icon: <IconCheck />,
                })
                reloadList()
                onCloseModal()
            } else {
                notifications.show({
                    title: 'Failed',
                    message: response.message || 'Operation failed',
                    color: 'red',
                    icon: <IconX />,
                })
            }
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: error.message || 'Internal Server Error',
                color: 'red',
                icon: <IconX />,
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box pos="relative">
            <LoadingOverlay visible={loading} overlayProps={{ radius: "sm", blur: 2 }} />

            <Box mb="md">
                <Select
                    label="Country"
                    placeholder={loadingCountries ? "Loading countries..." : "Select Country"}
                    data={countryList}
                    value={form.country_id}
                    onChange={(val) => handleChange('country_id', val)}
                    searchable
                    nothingFoundMessage="No country found"
                    renderOption={({ option, checked }) => {
                        const country = countryList.find(c => c.value === option.value)
                        return (
                            <Group flex="1" gap="xs">
                                {country?.iso_code_2 && (
                                    <Avatar src={`https://flagcdn.com/w40/${country.iso_code_2.toLowerCase()}.png`} alt={country.iso_code_2} size="sm" radius="xl" />
                                )}
                                <Text size="sm">{option.label}</Text>
                                {checked && <IconCheck style={{ marginInlineStart: 'auto' }} size={16} />}
                            </Group>
                        )
                    }}
                    leftSection={
                        (() => {
                            const selected = countryList.find(c => c.value === form.country_id)
                            return selected?.iso_code_2 ? (
                                <Avatar
                                    src={`https://flagcdn.com/w40/${selected.iso_code_2.toLowerCase()}.png`}
                                    size="xs"
                                    radius="xl"
                                />
                            ) : null
                        })()
                    }
                    error={errors.country_id}
                    withAsterisk
                    disabled={loadingCountries}
                    rightSection={loadingCountries ? <Loader size={16} /> : null}
                    mb="md"
                />

                <TextInput
                    label="Province Name"
                    placeholder="Enter province name"
                    value={form.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    error={errors.name}
                    withAsterisk
                />
            </Box>

            <Group justify="flex-end" mt="xl">
                <Button variant="default" onClick={onCloseModal}>Cancel</Button>
                <Button onClick={handleSubmit}>
                    {dataDetail ? "Update" : "Submit"}
                </Button>
            </Group>
        </Box>
    )
}

export default FormProvince
